/**
 * This component loads the next invoice for a given purchase, if there is one.
 * It also handles changing the payment method for that (and future) invoices.
 */
import { rpx } from 'client/lib/rpx-client';
import * as fmt from 'shared/payments/fmt';
import { useTryAsyncData } from 'client/lib/hooks';
import { DefaultSpinner } from '@components/spinner';
import { fmtFullDate } from 'shared/dateutil';
import { Button } from '@components/buttons';
import { ModalForm, showModalForm } from '@components/modal-form';
import { useCurrentUser } from '@components/router/session-context';
import { useState } from 'preact/hooks';
import { showError } from '@components/app-error';
import { StripeContext, StripeInput } from '@course/components/checkout/stripe';
import {
  buildStripeConfirmParams,
  describeCheckoutError,
  loggableCheckoutError,
} from '@course/components/checkout/checkout-logic';
import { reportCheckoutError } from '@course/components/checkout/report-checkout-error';
import { useIntl } from 'shared/intl/use-intl';
import { RUZUKU_ASSETS_BASE_URL } from 'shared/consts';

const store = rpx.upcomingInvoice;

function changePaymentMethod({
  purchaseId,
  priceId,
  details,
}: {
  purchaseId: string;
  priceId: string;
  details?: {
    cardLast4?: string;
    cardBrand?: string;
  };
}) {
  showModalForm(({ resolve }) => {
    const currentUser = useCurrentUser()!;

    const [stripeContext, setStripeContext] = useState<StripeContext | undefined>(undefined);
    const [cardError, setCardError] = useState<string | undefined>(undefined);

    // Create the setup intent (and learn the customer's saved billing address)
    // up front: the card form opts out of billing-details fields, so Stripe
    // requires a complete address in confirmParams. When the saved address
    // isn't confirm-ready (missing state — e.g. state-less countries) we mount
    // the Address Element, prefilled with whatever is on file. retryNonce
    // re-runs the fetch when the user retries after a failure.
    const [retryNonce, setRetryNonce] = useState(0);
    const intent = useTryAsyncData(
      () => store.createPaymentMethodChangeIntent({ purchaseId }),
      [purchaseId, retryNonce],
    );

    const saveCard = async () => {
      if (!stripeContext || !intent.data) {
        return;
      }
      try {
        setCardError(undefined);
        const addressElement = stripeContext.elements.getElement('address');
        const submitResult = await stripeContext.elements.submit();
        if (submitResult.error) {
          throw submitResult.error;
        }

        let address: Record<string, string | undefined> | undefined = intent.data.savedAddress;
        if (addressElement) {
          const addressFormState = await addressElement.getValue();
          if (!addressFormState.complete) {
            setCardError('Please complete your billing address.');
            return;
          }
          address = {
            ...addressFormState.value.address,
            line2: addressFormState.value.address?.line2 || undefined,
          };
        }

        const confirmResult = await stripeContext.stripe.confirmSetup({
          elements: stripeContext.elements,
          clientSecret: intent.data.clientSecret,
          confirmParams: buildStripeConfirmParams({
            returnUrl: window.location.href,
            userName: currentUser.name,
            address,
          }),
          redirect: 'if_required',
        });

        let setupIntent = confirmResult.setupIntent;
        if (confirmResult.error) {
          // A retry after a partial failure (card saved, RPC below failed) hits
          // the already-succeeded intent; Stripe rejects the re-confirm but
          // hands the intent back on the error. Proceed with it.
          const errIntent = (confirmResult.error as { setup_intent?: typeof setupIntent })
            .setup_intent;
          if (errIntent?.status === 'succeeded') {
            setupIntent = errIntent;
          } else {
            throw confirmResult.error;
          }
        }

        if (setupIntent?.payment_method) {
          const pmId =
            typeof setupIntent.payment_method === 'string'
              ? setupIntent.payment_method
              : setupIntent.payment_method.id;
          await store.changePaymentMethod({
            purchaseId,
            paymentMethod: pmId,
            details: details && { ...details, userId: currentUser.id },
          });
        }

        location.reload();
      } catch (err) {
        const described = describeCheckoutError(err);
        reportCheckoutError({ step: 'card_change', priceId: priceId, err });
        if (described.kind === 'card') {
          setCardError(described.message);
          console.warn('change payment method card error', loggableCheckoutError(err));
        } else {
          showError(err);
        }
      }
    };

    return (
      <ModalForm
        onClose={resolve}
        onSubmit={saveCard}
        title="Enter a new payment method"
        confirmButtonText="Save card"
      >
        {intent.isLoading && <DefaultSpinner />}
        {!intent.isLoading && !intent.data && (
          <div
            role="alert"
            class="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            We couldn't start the card update.{' '}
            <Button class="underline" onClick={() => setRetryNonce((n) => n + 1)}>
              Try again
            </Button>
          </div>
        )}
        {intent.data && (
          <StripeInput
            priceId={priceId}
            onReady={setStripeContext}
            collectAddress={intent.data.requiresAddress}
            defaultAddress={intent.data.savedAddress}
            customerName={currentUser.name}
            mode="setup"
          />
        )}
        {cardError && (
          <div
            role="alert"
            class="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {cardError}
          </div>
        )}
      </ModalForm>
    );
  });
}

function CardBrand({ brand }: { brand?: string }) {
  brand = brand || 'card';
  brand = brand.toLowerCase();

  if (!['amex', 'discover', 'visa', 'mastercard'].includes(brand)) {
    return <span class="border rounded-xs px-1 bg-gray-50 border-gray-200 mr-2">{brand}</span>;
  }

  // These are assets at a well-known location, and are not environment-specific,
  // so we're hard-coding the URL.
  return <img src={`${RUZUKU_ASSETS_BASE_URL}/logos/${brand}.svg`} alt={brand} class="w-8" />;
}

export function UpcomingInvoice({
  purchaseId,
  asSeller,
  isPaused,
}: {
  purchaseId: string;
  asSeller: boolean;
  /*
   * Stripe keeps generating the upcoming invoice for a subscription paused
   * with `behavior: 'void'` — verified against a live paused subscription,
   * which still returns amount_due 9700 — so this component would otherwise
   * show "Next payment $97 on <date>" directly under SubscriptionStatus
   * saying billing is paused. The paused line above already states what
   * happens next, so drop the amount rather than repeat it. Only that block:
   * a failed-payment retry notice still applies while paused.
   */
  isPaused: boolean;
}) {
  const intl = useIntl();
  const { isLoading, data: invoice } = useTryAsyncData(
    () => store.getStripeData({ purchaseId }),
    [purchaseId],
  );

  if (isLoading) {
    return <DefaultSpinner />;
  }

  if (!invoice) {
    return null;
  }

  const paymentProgress = invoice.numPaymentsRequired
    ? intl('{count:number} of {total:number}', {
        count: (invoice.numPaymentsMade || 0) + 1,
        total: invoice.numPaymentsRequired,
      })
    : '';
  const [nextAttemptPrefix, nextAttempt, nextAttemptSuffix] = intl.split(
    'The latest payment {paymentProgress:string} failed! The next attempt will be <>{date:string}</>.',
    {
      paymentProgress,
      date: fmtFullDate(invoice.dueDate),
    },
  );

  return (
    <div>
      {/*
       * Deliberately NOT gated on isPaused. `behavior: 'void'` voids invoices
       * generated during the pause, but an invoice already open when the pause
       * started keeps dunning — a member who paused after a payment failed
       * still needs to see the retry date.
       */}
      {invoice.attempted && (
        <p class="text-red-600">
          {nextAttemptPrefix}
          <strong>{nextAttempt}</strong>
          {nextAttemptSuffix}
        </p>
      )}
      {!invoice.attempted && !isPaused && (
        <>
          <h4 class="font-semibold mb-2">
            {intl('Next payment')} {paymentProgress}
          </h4>
          <div>
            <strong>
              {fmt.price({
                priceInCents: invoice.amount,
                currency: invoice.currency,
              })}
            </strong>
            <span class="ml-2">{fmtFullDate(invoice.dueDate)}</span>
          </div>
        </>
      )}
      <Button
        onClick={
          asSeller
            ? undefined
            : () =>
                changePaymentMethod({
                  purchaseId,
                  priceId: invoice.priceId,
                  details: {
                    cardLast4: invoice.cardLast4,
                    cardBrand: invoice.cardBrand,
                  },
                })
        }
        class="flex items-center my-2 space-x-2"
      >
        <CardBrand brand={invoice.cardBrand} />
        <span class="opacity-75">
          {intl('Ending in {last}', { last: invoice.cardLast4 || '****' })}
        </span>
        {!asSeller && <span class="text-indigo-600 dark:text-indigo-400">{intl('change')}</span>}
      </Button>
    </div>
  );
}
